import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Plus,
  Edit2,
  Trash2,
  X,
  Eye,
  Newspaper,
  MessageSquare,
  Check,
  Users,
  FileText,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import MediaUpload from "../components/MediaUpload";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState("news");

  useEffect(() => {
    if (!loading && !user) navigate("/admin");
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#0D2F4C]">
        Loading...
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#F8F9FA]">
      {/* Topbar */}
      <header className="bg-white border-b border-[#0D2F4C]/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <span className="hidden sm:block text-xs uppercase tracking-[0.25em] text-[#0D2F4C]/50">
              {t.admin.dashboard_title}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher variant="dark" />
            <span className="hidden sm:block text-sm text-[#4B5563]">{user.email}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={() => {
                logout();
                navigate("/admin");
              }}
              className="flex items-center gap-2 text-sm text-[#0D2F4C] hover:bg-[#0D2F4C] hover:text-white px-3 py-1.5 border border-[#0D2F4C]/30 transition-colors"
              style={{ borderRadius: 0 }}
            >
              <LogOut size={14} />
              <span>{t.admin.logout}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex gap-px bg-[#0D2F4C]/10 border border-[#0D2F4C]/10 mb-8 w-fit">
          <TabBtn
            active={tab === "news"}
            onClick={() => setTab("news")}
            icon={<Newspaper size={14} />}
            label={t.admin.news_tab}
            testid="admin-tab-news"
          />
          <TabBtn
            active={tab === "contact"}
            onClick={() => setTab("contact")}
            icon={<MessageSquare size={14} />}
            label={t.admin.contact_tab}
            testid="admin-tab-contact"
          />
          <TabBtn
            active={tab === "team"}
            onClick={() => setTab("team")}
            icon={<Users size={14} />}
            label={t.admin.team_tab}
            testid="admin-tab-team"
          />
          <TabBtn
            active={tab === "legal"}
            onClick={() => setTab("legal")}
            icon={<FileText size={14} />}
            label={t.admin.legal_tab}
            testid="admin-tab-legal"
          />
        </div>

        {tab === "news" && <NewsTab />}
        {tab === "contact" && <ContactTab />}
        {tab === "team" && <TeamTab />}
        {tab === "legal" && <LegalTab />}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors ${
      active
        ? "bg-[#0D2F4C] text-white"
        : "bg-white text-[#0D2F4C] hover:bg-[#0D2F4C]/5"
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);

// ============================================================
// News Tab
// ============================================================
function NewsTab() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/news");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    await api.delete(`/admin/news/${id}`);
    load();
  };

  const handleEdit = (item) => {
    setEditing(item);
    setShowForm(true);
  };

  const handleNew = () => {
    setEditing(null);
    setShowForm(true);
  };

  return (
    <div data-testid="admin-news-tab">
      <div className="flex justify-between items-center mb-6">
        <h2
          className="font-serif text-2xl text-[#0D2F4C]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {t.admin.news_tab}
        </h2>
        <button
          data-testid="admin-new-news-btn"
          onClick={handleNew}
          className="flex items-center gap-2 bg-[#0D2F4C] text-white px-5 py-2.5 text-sm hover:bg-[#0A243A] transition-colors"
          style={{ borderRadius: 0 }}
        >
          <Plus size={16} />
          <span>{t.admin.new_article}</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#4B5563]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#0D2F4C]/10 text-[#4B5563]">
          {t.admin.no_news}
        </div>
      ) : (
        <div className="bg-white border border-[#0D2F4C]/10">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#0D2F4C]/10 text-xs uppercase tracking-wider text-[#0D2F4C]/60">
                <th className="text-left p-4">Title (FR)</th>
                <th className="text-left p-4 hidden md:table-cell">Category</th>
                <th className="text-left p-4 hidden md:table-cell">Status</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`admin-news-row-${it.id}`}
                  className="border-b border-[#0D2F4C]/5 hover:bg-[#F8F9FA]"
                >
                  <td className="p-4 text-sm text-[#0D2F4C]">{it.title_fr}</td>
                  <td className="p-4 text-sm text-[#4B5563] hidden md:table-cell">
                    {it.category_fr}
                  </td>
                  <td className="p-4 text-xs hidden md:table-cell">
                    <span
                      className={`inline-block px-2 py-0.5 ${
                        it.published
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-gray-50 text-gray-600 border border-gray-200"
                      }`}
                    >
                      {it.published ? t.admin.published : t.admin.draft}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        data-testid={`admin-edit-news-${it.id}`}
                        onClick={() => handleEdit(it)}
                        className="p-2 text-[#0D2F4C] hover:bg-[#0D2F4C]/10"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        data-testid={`admin-delete-news-${it.id}`}
                        onClick={() => handleDelete(it.id)}
                        className="p-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <NewsForm
          item={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function NewsForm({ item, onClose, onSaved }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(
    item || {
      title_fr: "",
      title_en: "",
      excerpt_fr: "",
      excerpt_en: "",
      content_fr: "",
      content_en: "",
      category_fr: "",
      category_en: "",
      image_url: "",
      video_url: "",
      published: true,
    }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (item) {
        await api.put(`/admin/news/${item.id}`, form);
      } else {
        await api.post("/admin/news", form);
      }
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div
        data-testid="admin-news-form"
        className="bg-white max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#0D2F4C]/10 sticky top-0 bg-white">
          <h3
            className="font-serif text-xl text-[#0D2F4C]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {item ? t.admin.edit : t.admin.new_article}
          </h3>
          <button
            data-testid="admin-news-form-close"
            onClick={onClose}
            className="p-2 hover:bg-[#0D2F4C]/10"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title (FR)" name="title_fr" value={form.title_fr} onChange={onChange} required testid="form-title-fr" />
            <Field label="Title (EN)" name="title_en" value={form.title_en} onChange={onChange} required testid="form-title-en" />
            <Field label="Category (FR)" name="category_fr" value={form.category_fr} onChange={onChange} required testid="form-cat-fr" />
            <Field label="Category (EN)" name="category_en" value={form.category_en} onChange={onChange} required testid="form-cat-en" />
          </div>
          <TextArea label="Excerpt (FR)" name="excerpt_fr" value={form.excerpt_fr} onChange={onChange} required testid="form-excerpt-fr" />
          <TextArea label="Excerpt (EN)" name="excerpt_en" value={form.excerpt_en} onChange={onChange} required testid="form-excerpt-en" />
          <TextArea label="Content (FR)" name="content_fr" value={form.content_fr} onChange={onChange} rows={5} required testid="form-content-fr" />
          <TextArea label="Content (EN)" name="content_en" value={form.content_en} onChange={onChange} rows={5} required testid="form-content-en" />

          <div className="border-t border-[#0D2F4C]/10 pt-5 space-y-5">
            <MediaUpload
              kind="image"
              label="Image de couverture"
              value={form.image_url}
              onChange={(url) => setForm((p) => ({ ...p, image_url: url }))}
              testidPrefix="form-image"
            />

            {/* External video URL (Drive, Dropbox, YouTube, Vimeo, direct MP4...) */}
            <div data-testid="form-video-url-wrapper">
              <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
                Lien vidéo externe (optionnel)
              </label>
              <input
                type="url"
                name="video_url"
                value={form.video_url}
                onChange={onChange}
                placeholder="https://drive.google.com/... · https://youtu.be/... · https://dropbox.com/..."
                className="w-full bg-white text-[#0D2F4C] border border-[#0D2F4C]/20 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0D2F4C] transition-colors"
                style={{ borderRadius: 0 }}
                data-testid="form-video-url"
              />
              <p className="mt-2 text-xs text-[#4B5563] leading-relaxed">
                Collez l'URL d'une vidéo hébergée sur Google Drive, Dropbox, YouTube, Vimeo,
                ou tout autre hébergeur. Un bouton « Regarder la vidéo » sera affiché en haut
                de l'article — pas d'upload nécessaire.
              </p>
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="published"
              checked={form.published}
              onChange={onChange}
              data-testid="form-published"
              className="w-4 h-4 accent-[#0D2F4C]"
            />
            <span className="text-sm text-[#0D2F4C]">{t.admin.published}</span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-[#0D2F4C]/10">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm border border-[#0D2F4C]/30 text-[#0D2F4C] hover:bg-[#0D2F4C]/5"
              style={{ borderRadius: 0 }}
            >
              {t.admin.cancel}
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="admin-news-save-btn"
              className="px-5 py-2.5 text-sm bg-[#0D2F4C] text-white hover:bg-[#0A243A] disabled:opacity-50"
              style={{ borderRadius: 0 }}
            >
              {saving ? "..." : t.admin.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, name, value, onChange, required, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
      {label}
      {required && " *"}
    </label>
    <input
      name={name}
      value={value || ""}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full px-3 py-2 bg-white border border-[#0D2F4C]/20 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C] text-sm"
    />
  </div>
);

const TextArea = ({ label, name, value, onChange, required, rows = 2, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
      {label}
      {required && " *"}
    </label>
    <textarea
      name={name}
      value={value || ""}
      onChange={onChange}
      required={required}
      rows={rows}
      data-testid={testid}
      className="w-full px-3 py-2 bg-white border border-[#0D2F4C]/20 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C] text-sm resize-none"
    />
  </div>
);

// ============================================================
// Contact Tab
// ============================================================
function ContactTab() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/contact");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (id) => {
    await api.patch(`/admin/contact/${id}/read`);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    await api.delete(`/admin/contact/${id}`);
    setSelected(null);
    load();
  };

  return (
    <div data-testid="admin-contact-tab">
      <h2
        className="font-serif text-2xl text-[#0D2F4C] mb-6"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {t.admin.contact_tab}
      </h2>

      {loading ? (
        <div className="text-center py-12 text-[#4B5563]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#0D2F4C]/10 text-[#4B5563]">
          {t.admin.no_messages}
        </div>
      ) : (
        <div className="bg-white border border-[#0D2F4C]/10">
          {items.map((m) => (
            <div
              key={m.id}
              data-testid={`admin-contact-row-${m.id}`}
              className="border-b border-[#0D2F4C]/5 last:border-0 p-5 hover:bg-[#F8F9FA] transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    {!m.read && (
                      <span className="w-2 h-2 bg-[#0D2F4C] rounded-full" />
                    )}
                    <span className="font-medium text-[#0D2F4C]">{m.name}</span>
                    <span className="text-sm text-[#4B5563]">·</span>
                    <a
                      href={`mailto:${m.email}`}
                      className="text-sm text-[#4B5563] hover:text-[#0D2F4C]"
                    >
                      {m.email}
                    </a>
                  </div>
                  <div className="text-sm text-[#0D2F4C] mb-1">
                    <strong>{m.subject}</strong>
                    {m.company && (
                      <span className="text-[#4B5563]"> · {m.company}</span>
                    )}
                  </div>
                  <p className="text-sm text-[#4B5563] line-clamp-2">{m.message}</p>
                  <div className="text-xs text-[#4B5563]/60 mt-2">
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`admin-view-contact-${m.id}`}
                    onClick={() => setSelected(m)}
                    className="p-2 text-[#0D2F4C] hover:bg-[#0D2F4C]/10"
                  >
                    <Eye size={14} />
                  </button>
                  {!m.read && (
                    <button
                      data-testid={`admin-mark-read-${m.id}`}
                      onClick={() => markRead(m.id)}
                      className="p-2 text-green-700 hover:bg-green-50"
                      title={t.admin.mark_read}
                    >
                      <Check size={14} />
                    </button>
                  )}
                  <button
                    data-testid={`admin-delete-contact-${m.id}`}
                    onClick={() => handleDelete(m.id)}
                    className="p-2 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white max-w-2xl w-full p-8 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3
                  className="font-serif text-2xl text-[#0D2F4C]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {selected.subject}
                </h3>
                <p className="text-sm text-[#4B5563] mt-1">
                  {selected.name} · {selected.email}
                </p>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="admin-contact-close-btn"
                className="p-2 hover:bg-[#0D2F4C]/10"
              >
                <X size={18} />
              </button>
            </div>
            <div className="text-[#0D2F4C] whitespace-pre-wrap leading-relaxed">
              {selected.message}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Team Tab — Leadership members
// ============================================================
function TeamTab() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/team");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    await api.delete(`/admin/team/${id}`);
    load();
  };

  return (
    <div data-testid="admin-team-tab">
      <div className="flex justify-between items-center mb-6">
        <h2
          className="font-serif text-2xl text-[#0D2F4C]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {t.admin.team_tab}
        </h2>
        <button
          data-testid="admin-new-team-btn"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 bg-[#0D2F4C] text-white px-5 py-2.5 text-sm hover:bg-[#0A243A] transition-colors"
          style={{ borderRadius: 0 }}
        >
          <Plus size={16} />
          <span>{t.admin.new_member}</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#4B5563]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#0D2F4C]/10 text-[#4B5563]">
          {t.admin.no_team}
        </div>
      ) : (
        <div className="bg-white border border-[#0D2F4C]/10">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#0D2F4C]/10 text-xs uppercase tracking-wider text-[#0D2F4C]/60">
                <th className="text-left p-4">Photo</th>
                <th className="text-left p-4">Nom</th>
                <th className="text-left p-4 hidden md:table-cell">Titre (FR)</th>
                <th className="text-left p-4 hidden md:table-cell">Ordre</th>
                <th className="text-left p-4 hidden md:table-cell">Statut</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr
                  key={m.id}
                  data-testid={`admin-team-row-${m.id}`}
                  className="border-b border-[#0D2F4C]/5 hover:bg-[#F8F9FA]"
                >
                  <td className="p-4">
                    {m.photo_url ? (
                      <img
                        src={m.photo_url}
                        alt={m.name}
                        className="w-12 h-12 object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center bg-[#0D2F4C] text-[#C9A961] font-serif text-sm">
                        {m.initials || m.name?.[0]}
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-sm text-[#0D2F4C] font-medium">{m.name}</td>
                  <td className="p-4 text-sm text-[#4B5563] hidden md:table-cell">{m.title_fr}</td>
                  <td className="p-4 text-sm text-[#4B5563] hidden md:table-cell">{m.order}</td>
                  <td className="p-4 text-xs hidden md:table-cell">
                    <span
                      className={`inline-block px-2 py-0.5 ${
                        m.published
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-gray-50 text-gray-600 border border-gray-200"
                      }`}
                    >
                      {m.published ? t.admin.published : t.admin.draft}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        data-testid={`admin-edit-team-${m.id}`}
                        onClick={() => {
                          setEditing(m);
                          setShowForm(true);
                        }}
                        className="p-2 text-[#0D2F4C] hover:bg-[#0D2F4C]/10"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        data-testid={`admin-delete-team-${m.id}`}
                        onClick={() => handleDelete(m.id)}
                        className="p-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <TeamForm
          item={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function TeamForm({ item, onClose, onSaved }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(
    item || {
      name: "",
      title_fr: "",
      title_en: "",
      bio_fr: "",
      bio_en: "",
      photo_url: "",
      initials: "",
      order: 1,
      published: true,
    }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : name === "order" ? parseInt(value || "0", 10) : value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (item) await api.put(`/admin/team/${item.id}`, form);
      else await api.post("/admin/team", form);
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div data-testid="admin-team-form" className="bg-white max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#0D2F4C]/10 sticky top-0 bg-white">
          <h3 className="font-serif text-xl text-[#0D2F4C]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {item ? t.admin.edit : t.admin.new_member}
          </h3>
          <button data-testid="admin-team-form-close" onClick={onClose} className="p-2 hover:bg-[#0D2F4C]/10">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom complet" name="name" value={form.name} onChange={onChange} required testid="team-name" />
            <Field label="Initiales (ex. DT)" name="initials" value={form.initials} onChange={onChange} testid="team-initials" />
            <Field label="Titre (FR)" name="title_fr" value={form.title_fr} onChange={onChange} required testid="team-title-fr" />
            <Field label="Titre (EN)" name="title_en" value={form.title_en} onChange={onChange} required testid="team-title-en" />
          </div>
          <TextArea label="Bio (FR)" name="bio_fr" value={form.bio_fr} onChange={onChange} rows={4} required testid="team-bio-fr" />
          <TextArea label="Bio (EN)" name="bio_en" value={form.bio_en} onChange={onChange} rows={4} required testid="team-bio-en" />

          <div className="border-t border-[#0D2F4C]/10 pt-5">
            <MediaUpload
              kind="image"
              label="Photo de profil (carrée recommandée)"
              value={form.photo_url}
              onChange={(url) => setForm((p) => ({ ...p, photo_url: url }))}
              testidPrefix="team-photo"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 items-end">
            <Field label="Ordre d'affichage" name="order" value={form.order} onChange={onChange} testid="team-order" />
            <label className="flex items-center gap-3 cursor-pointer pb-2">
              <input
                type="checkbox"
                name="published"
                checked={form.published}
                onChange={onChange}
                data-testid="team-published"
                className="w-4 h-4 accent-[#0D2F4C]"
              />
              <span className="text-sm text-[#0D2F4C]">{t.admin.published}</span>
            </label>
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

          <div className="flex gap-3 pt-4 border-t border-[#0D2F4C]/10">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm border border-[#0D2F4C]/30 text-[#0D2F4C] hover:bg-[#0D2F4C]/5" style={{ borderRadius: 0 }}>
              {t.admin.cancel}
            </button>
            <button type="submit" disabled={saving} data-testid="admin-team-save-btn" className="px-5 py-2.5 text-sm bg-[#0D2F4C] text-white hover:bg-[#0A243A] disabled:opacity-50" style={{ borderRadius: 0 }}>
              {saving ? "..." : t.admin.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Legal Documents Tab
// ============================================================
const DOC_TYPE_LABELS = {
  escrow: { label: "Escrow", color: "bg-rose-50 text-rose-700 border-rose-200" },
  partnership: { label: "Partnership", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  procedure: { label: "Procédure", color: "bg-blue-50 text-blue-700 border-blue-200" },
  procedure_confidential: { label: "Procédure (Conf.)", color: "bg-amber-50 text-amber-800 border-amber-300" },
  esg_report: { label: "Rapport ESG", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  identity_kit: { label: "Identité", color: "bg-purple-50 text-purple-700 border-purple-200" },
  other: { label: "Autre", color: "bg-gray-50 text-gray-700 border-gray-200" },
};

const STATUS_LABELS = {
  draft: { label: "Brouillon", color: "bg-gray-50 text-gray-700 border-gray-200" },
  sent: { label: "Envoyé", color: "bg-blue-50 text-blue-700 border-blue-200" },
  signed: { label: "Signé", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  published: { label: "Publié", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  archived: { label: "Archivé", color: "bg-gray-50 text-gray-500 border-gray-200" },
};

// Types supporting backend regeneration (any of these can be re-built)
const REGEN_SUPPORTED = new Set([
  "partnership",
  "procedure_confidential",
  "identity_kit",
  "procedure",
  "esg_report",
]);
// Subset that also re-sends the document by email via Resend
const REGEN_SENDS_EMAIL = new Set([
  "partnership",
  "procedure_confidential",
  "identity_kit",
]);

function LegalTab() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/legal-documents");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    await api.delete(`/admin/legal-documents/${id}`);
    load();
  };

  const handleRegenerate = async (doc) => {
    const sendInfo = REGEN_SENDS_EMAIL.has(doc.doc_type);
    const confirmMsg = sendInfo
      ? `Régénérer et renvoyer "${doc.reference}" par email ?\n\nDestinataires : ${(doc.recipients || []).join(", ") || "(défaut du script)"}`
      : `Régénérer le PDF "${doc.reference}" ? (Pas d'envoi email pour ce type)`;
    if (!window.confirm(confirmMsg)) return;
    setRegenId(doc.id);
    setFeedback(null);
    try {
      const res = await api.post(`/admin/legal-documents/${doc.id}/regenerate`);
      setFeedback({
        id: doc.id,
        type: "success",
        message: sendInfo
          ? `Renvoyé avec succès. Resend id : ${res.data.resend_id || "—"}`
          : "PDF régénéré.",
      });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Erreur inconnue";
      setFeedback({
        id: doc.id,
        type: "error",
        message: String(detail).slice(0, 300),
      });
    } finally {
      setRegenId(null);
      setTimeout(() => setFeedback(null), 8000);
    }
  };

  return (
    <div data-testid="admin-legal-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl text-[#0D2F4C]" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t.admin.legal_tab}
        </h2>
        <button
          data-testid="admin-new-legal-btn"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 bg-[#0D2F4C] text-white px-5 py-2.5 text-sm hover:bg-[#0A243A] transition-colors"
          style={{ borderRadius: 0 }}
        >
          <Plus size={16} />
          <span>{t.admin.new_document}</span>
        </button>
      </div>

      {feedback && (
        <div
          data-testid="admin-legal-feedback"
          className={`mb-5 px-4 py-3 text-sm border ${
            feedback.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {feedback.message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-[#4B5563]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#0D2F4C]/10 text-[#4B5563]">
          {t.admin.no_legal}
        </div>
      ) : (
        <div className="bg-white border border-[#0D2F4C]/10 overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-[#0D2F4C]/10 text-xs uppercase tracking-wider text-[#0D2F4C]/60">
                <th className="text-left p-4">Référence</th>
                <th className="text-left p-4">Titre (FR)</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Destinataires</th>
                <th className="text-left p-4">Statut</th>
                <th className="text-left p-4">Date</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const tp = DOC_TYPE_LABELS[d.doc_type] || DOC_TYPE_LABELS.other;
                const st = STATUS_LABELS[d.status] || STATUS_LABELS.draft;
                const sentDate = d.sent_at || d.created_at;
                return (
                  <tr key={d.id} data-testid={`admin-legal-row-${d.id}`} className="border-b border-[#0D2F4C]/5 hover:bg-[#F8F9FA] align-top">
                    <td className="p-4 text-xs font-mono text-[#0D2F4C]">{d.reference}</td>
                    <td className="p-4 text-sm text-[#0D2F4C] max-w-xs">{d.title_fr}</td>
                    <td className="p-4 text-xs">
                      <span className={`inline-block px-2 py-0.5 border ${tp.color}`}>{tp.label}</span>
                    </td>
                    <td className="p-4 text-xs text-[#4B5563]">
                      {(d.recipients || []).length === 0
                        ? <span className="text-[#4B5563]/50 italic">— public —</span>
                        : (d.recipients || []).map((r) => <div key={r}>{r}</div>)}
                    </td>
                    <td className="p-4 text-xs">
                      <span className={`inline-block px-2 py-0.5 border ${st.color}`}>{st.label}</span>
                    </td>
                    <td className="p-4 text-xs text-[#4B5563]">
                      {sentDate ? new Date(sentDate).toLocaleDateString() : "—"}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        {REGEN_SUPPORTED.has(d.doc_type) && (
                          <button
                            data-testid={`admin-regen-legal-${d.id}`}
                            onClick={() => handleRegenerate(d)}
                            disabled={regenId === d.id}
                            className="p-2 text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={REGEN_SENDS_EMAIL.has(d.doc_type) ? "Régénérer & renvoyer par email" : "Régénérer le PDF"}
                          >
                            <RefreshCw size={14} className={regenId === d.id ? "animate-spin" : ""} />
                          </button>
                        )}
                        {d.resend_id && (
                          <a
                            href={`https://resend.com/emails/${d.resend_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`admin-resend-link-${d.id}`}
                            className="p-2 text-[#0D2F4C] hover:bg-[#0D2F4C]/10"
                            title="Voir sur Resend"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                        <button
                          data-testid={`admin-edit-legal-${d.id}`}
                          onClick={() => {
                            setEditing(d);
                            setShowForm(true);
                          }}
                          className="p-2 text-[#0D2F4C] hover:bg-[#0D2F4C]/10"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          data-testid={`admin-delete-legal-${d.id}`}
                          onClick={() => handleDelete(d.id)}
                          className="p-2 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <LegalForm
          item={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function LegalForm({ item, onClose, onSaved }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(
    item
      ? { ...item, recipients_text: (item.recipients || []).join(", ") }
      : {
          reference: "",
          title_fr: "",
          title_en: "",
          doc_type: "partnership",
          recipients_text: "",
          sent_at: "",
          resend_id: "",
          status: "sent",
          notes: "",
          file_path: "",
        }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        recipients: form.recipients_text
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      delete payload.recipients_text;
      if (!payload.sent_at) delete payload.sent_at;

      if (item) await api.put(`/admin/legal-documents/${item.id}`, payload);
      else await api.post("/admin/legal-documents", payload);
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div data-testid="admin-legal-form" className="bg-white max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#0D2F4C]/10 sticky top-0 bg-white">
          <h3 className="font-serif text-xl text-[#0D2F4C]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {item ? t.admin.edit : t.admin.new_document}
          </h3>
          <button data-testid="admin-legal-form-close" onClick={onClose} className="p-2 hover:bg-[#0D2F4C]/10">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Référence" name="reference" value={form.reference} onChange={onChange} required testid="legal-ref" />
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">Type *</label>
              <select
                name="doc_type"
                value={form.doc_type}
                onChange={onChange}
                required
                data-testid="legal-type"
                className="w-full px-3 py-2 bg-white border border-[#0D2F4C]/20 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C] text-sm"
              >
                {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <Field label="Titre (FR)" name="title_fr" value={form.title_fr} onChange={onChange} required testid="legal-title-fr" />
            <Field label="Titre (EN)" name="title_en" value={form.title_en} onChange={onChange} required testid="legal-title-en" />
          </div>

          <TextArea label="Destinataires (séparés par virgule)" name="recipients_text" value={form.recipients_text} onChange={onChange} rows={2} testid="legal-recipients" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Date d'envoi (ISO)" name="sent_at" value={form.sent_at} onChange={onChange} testid="legal-sent-at" />
            <Field label="Resend ID" name="resend_id" value={form.resend_id} onChange={onChange} testid="legal-resend-id" />
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">Statut *</label>
              <select
                name="status"
                value={form.status}
                onChange={onChange}
                required
                data-testid="legal-status"
                className="w-full px-3 py-2 bg-white border border-[#0D2F4C]/20 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C] text-sm"
              >
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          <Field label="Chemin du fichier (optionnel)" name="file_path" value={form.file_path} onChange={onChange} testid="legal-file-path" />
          <TextArea label="Notes" name="notes" value={form.notes} onChange={onChange} rows={3} testid="legal-notes" />

          {error && <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

          <div className="flex gap-3 pt-4 border-t border-[#0D2F4C]/10">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm border border-[#0D2F4C]/30 text-[#0D2F4C] hover:bg-[#0D2F4C]/5" style={{ borderRadius: 0 }}>
              {t.admin.cancel}
            </button>
            <button type="submit" disabled={saving} data-testid="admin-legal-save-btn" className="px-5 py-2.5 text-sm bg-[#0D2F4C] text-white hover:bg-[#0A243A] disabled:opacity-50" style={{ borderRadius: 0 }}>
              {saving ? "..." : t.admin.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
